import { ISortableTableItem } from '../DataModelerTablesGrid/types';
import {
  FieldClass,
  IColumnWithTypeInput,
  IDataModelPreviewColumnsDataQuery,
  IDataModelPreviewColumnsDataQueryVariables,
  ISchemaDataModelFieldsFragment,
  ITableColumnFieldsFragment,
} from '../../../../utils/apollo/graphqlSchema';
import { useCallback, useMemo, useState } from 'react';
import { convertPreviewColumnDataToRows, IPreviewDataRows } from '../utils';
import { dataModelerSelectCurrentDraftEdits } from '../../../../redux/selectors/dataModeler';
import { useAppSelector } from '../../../../redux/hooks';
import { useCdwTableColumnsData } from '../DataModelerCdwTableGrid';
import { ApolloError, useLazyQuery } from '@apollo/client';
import { DATA_MODEL_PREVIEW_COLUMNS_DATA } from '../../../../utils/apollo/dataModeler/queries';
import { typeParamsToTypeParamsInput } from '../../../../redux/slices/dataModeler/utils';
import { ICdwTableColumnData } from '../DataModelerCdwTableGrid/types';

interface IProps {
  schemaObj: ISchemaDataModelFieldsFragment;
  tableItem: ISortableTableItem;
  availableColumnsFromServer: ITableColumnFieldsFragment[];
}

const PREVIEW_TABLE_COLUMNS_DATA_DISPLAY_LIMIT = 100;

const sortMappedBeforeNotMapped = (
  a: ICdwTableColumnData,
  b: ICdwTableColumnData,
) => {
  if (
    (a.addFieldInput || a.extendFieldInput) &&
    !(b.addFieldInput || b.extendFieldInput)
  ) {
    return -1;
  }
  if (a.fieldMeta && !b.fieldMeta) {
    return -1;
  }
  return 1;
};

const useDataModelerCdwTablePreviewGridModel = ({
  schemaObj,
  tableItem,
  availableColumnsFromServer,
}: IProps) => {
  const draftEdits = useAppSelector(dataModelerSelectCurrentDraftEdits);
  const [previewCachedData, setPreviewCachedData] =
    useState<IPreviewDataRows | null>(null);
  const { tableKey, viewKey } = tableItem;

  const isExistingTable =
    schemaObj?.fact?.tableKeys.includes(tableKey) ||
    !!schemaObj?.dimensions?.find((dim) => dim.tableKeys.includes(tableKey));

  const { columns } = useCdwTableColumnsData({
    availableColumnsFromServer,
    viewKey,
    tableKey,
    isEditMode: false,
  });

  const [customInputs, selectInputs] = useMemo((): [
    IColumnWithTypeInput[],
    IColumnWithTypeInput[],
  ] => {
    const customInputs: IColumnWithTypeInput[] = [];
    const selectInputs = columns
      .sort(sortMappedBeforeNotMapped)
      .map((columnData) => {
        const customTypeInput =
          columnData.typeInput ??
          columnData.addFieldInput?.typeInput ??
          columnData.extendFieldInput?.typeInput;
        const typeInput = customTypeInput ?? {
          fieldClass: FieldClass.Basic,
          type: columnData.type,
          typeParams: typeParamsToTypeParamsInput(
            columnData.tableColumn.typeParams,
          ),
        };
        const ret = {
          column: columnData.column,
          typeInput,
        };
        if (customTypeInput) {
          customInputs.push(ret);
        }
        return ret;
      });
    return [customInputs, selectInputs];
  }, [columns]);

  const addedTable = useMemo(
    () =>
      isExistingTable
        ? null
        : draftEdits?.addedFactsData?.find(
            (fact) => fact.tableKey === tableKey,
          ) ??
          draftEdits?.addedDimensionsData?.find(
            (dim) => dim.tableKey === tableKey,
          ),
    [
      draftEdits?.addedDimensionsData,
      draftEdits?.addedFactsData,
      isExistingTable,
      tableKey,
    ],
  );

  const onCompleted = useCallback((data: IDataModelPreviewColumnsDataQuery) => {
    const { columns, errors, sqlQuery } = data.dataModelPreviewColumnsData;
    setPreviewCachedData(
      convertPreviewColumnDataToRows(
        columns,
        errors,
        sqlQuery,
        PREVIEW_TABLE_COLUMNS_DATA_DISPLAY_LIMIT,
      ),
    );
  }, []);

  const onError = useCallback((error: ApolloError) => {
    setPreviewCachedData({
      header: [],
      rows: [],
      sqlQuery: null,
      errors: ['Unexpected error', error.message],
    });
  }, []);

  const [previewDataQuery, { loading }] = useLazyQuery<
    IDataModelPreviewColumnsDataQuery,
    IDataModelPreviewColumnsDataQueryVariables
  >(DATA_MODEL_PREVIEW_COLUMNS_DATA);

  const loadPreviewData = useCallback(async () => {
    await previewDataQuery({
      variables: {
        schemaKey: draftEdits?.isNew ? null : schemaObj?.key ?? null,
        tableKey: isExistingTable ? tableKey : null,
        connection: draftEdits?.dataSource?.connection ?? null,
        sqlTableName: addedTable?.sqlTableName ?? null,
        draftColumns: customInputs,
        selectColumns: selectInputs,
        expandReferences: false,
        limit: PREVIEW_TABLE_COLUMNS_DATA_DISPLAY_LIMIT,
      },
      onError,
      onCompleted,
    });
  }, [
    addedTable?.sqlTableName,
    customInputs,
    draftEdits,
    isExistingTable,
    onCompleted,
    onError,
    previewDataQuery,
    schemaObj?.key,
    selectInputs,
    tableKey,
  ]);

  const resetPreviewData = useCallback(() => setPreviewCachedData(null), []);
  return {
    loading,
    previewCachedData,
    loadPreviewData,
    resetPreviewData,
  };
};

export default useDataModelerCdwTablePreviewGridModel;
